import { FilePlus2, Search, TerminalSquare } from 'lucide-react'
import type { ComponentProps, RefObject } from 'react'
import { Button } from '../hybrid'
import type { buildKnowledgeLibraryState } from '../../lib/knowledge-library-state'
import { KnowledgeSourceOverview } from './knowledge-source-overview'

type LibraryStats = ReturnType<typeof buildKnowledgeLibraryState>['stats']
type SourceOverview = ComponentProps<typeof KnowledgeSourceOverview>['overview']

export function KnowledgeLibraryHero({
  query,
  onQueryChange,
}: {
  query: string
  onQueryChange: (query: string) => void
}) {
  return (
    <section className="aw-knowledge-hero" aria-labelledby="knowledge-title">
      <div>
        <span className="aw-label">Knowledge library</span>
        <h1 id="knowledge-title">Knowledge</h1>
        <p>
          Browse the latest loaded Manual documents and Source-backed knowledge. Ownership, access,
          authority, and source sync health remain independent.
        </p>
      </div>
      <label className="aw-knowledge-search">
        <Search className="h-4 w-4" aria-hidden />
        <span className="sr-only">Filter loaded knowledge</span>
        <input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Filter the latest loaded results..."
        />
      </label>
    </section>
  )
}

export function KnowledgeLibraryLoadedSummary({
  stats,
  workspaceTotal,
  sourceOverview,
}: {
  stats: LibraryStats
  workspaceTotal: number
  sourceOverview: SourceOverview
}) {
  const loadedSummary = `Showing and filtering the latest ${stats.loaded} loaded results${
    workspaceTotal > stats.loaded ? ` of ${workspaceTotal} workspace documents` : ''
  }.`

  return (
    <>
      <p className="aw-knowledge-loaded-note">
        {loadedSummary}{' '}
        {workspaceTotal > stats.loaded && (
          <>
            Use <a href="/search">Search</a> to retrieve beyond this loaded set.
          </>
        )}
      </p>
      <section className="aw-knowledge-stats" aria-label="Loaded knowledge status">
        <div>
          <span>Loaded Source-backed</span>
          <strong>{stats.sourceBacked}</strong>
        </div>
        <div>
          <span>Loaded verified</span>
          <strong>{stats.synced}</strong>
        </div>
        <div>
          <span>Loaded sync warnings</span>
          <strong>{stats.syncWarnings}</strong>
        </div>
        <div>
          <span>Loaded Manual</span>
          <strong>{stats.manual}</strong>
        </div>
      </section>
      <KnowledgeSourceOverview overview={sourceOverview} />
    </>
  )
}

export function KnowledgeLibraryResultsHeader({
  countSummary,
  title,
  canCreateManual,
  isCreating,
  onCreateManual,
  headingRef,
}: {
  countSummary: string
  title: string
  canCreateManual: boolean
  isCreating: boolean
  onCreateManual: () => void
  headingRef?: RefObject<HTMLHeadingElement | null>
}) {
  return (
    <div className="aw-knowledge-results-head">
      <div>
        <span className="aw-label">{countSummary}</span>
        <h2 ref={headingRef} tabIndex={-1}>
          {title}
        </h2>
      </div>
      <div className="aw-knowledge-actions">
        {canCreateManual && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={isCreating}
            onClick={onCreateManual}
          >
            <FilePlus2 className="h-3.5 w-3.5" />
            New Manual document
          </Button>
        )}
        <Button to="/docs/cli" size="sm">
          <TerminalSquare className="h-3.5 w-3.5" />
          Sync sources
        </Button>
        <Button to="/search" size="sm">
          <Search className="h-3.5 w-3.5" />
          Search workspace
        </Button>
      </div>
    </div>
  )
}
